import React, { useEffect, useMemo, useRef } from "react";
import AutoResizeTextarea from "../AutoResizeTextarea";

const PanelSocial = ({
  indicators,
  isDirty,
  setIsDirty,
  editableResult,
  setEditableResult,
}) => {
  const barChartRef = useRef(null);
  const barInstance = useRef(null);

  // ── Extraction des données ──
  const client = indicators?.client || {};
  const cr = indicators?.compte_de_resultat || {};
  const dsn = indicators?.dsn || {};
  const dsnEffectif = dsn?.effectif || {};
  const dsnPeriode = dsn?.periode || {};

  const exerciceN = client.exercice_N || "N";
  const exerciceN1 = client.exercice_N1 || "N-1";

  const caN = cr.ca_N || 0;
  const caN1 = cr.ca_N1 || 0;
  const chargesPersonnelN = cr.charges_personnel_N || 0;
  const chargesPersonnelN1 = cr.charges_personnel_N1 || 0;
  const chargesPersonnelVarPct = cr.charges_personnel_var_pct || 0;
  const valeurAjouteeN = cr.valeur_ajoutee_N || 0;
  const valeurAjouteeN1 = cr.valeur_ajoutee_N1 || 0;

  // ── Tri des employés par coût employeur décroissant ──
  const dsnEmployes = useMemo(() => {
    const raw = indicators?.dsn?.employes || [];
    return [...raw].sort(
      (a, b) => (b.cout_employeur || 0) - (a.cout_employeur || 0)
    );
  }, [indicators]);

  // ── Calculs DSN ──
  const dsnTotalBrut = dsnEmployes.reduce(
    (s, e) => s + (e.salaire_brut || 0),
    0
  );
  const dsnTotalCotisationsSal = dsnEmployes.reduce(
    (s, e) => s + (e.cotisations_salariales || 0),
    0
  );
  const dsnTotalCotisationsPat = dsnEmployes.reduce(
    (s, e) => s + (e.cotisations_patronales || 0),
    0
  );
  const dsnTotalNet = dsnEmployes.reduce((s, e) => s + (e.net_a_payer || 0), 0);
  const dsnTotalCoutEmployeur = dsnEmployes.reduce(
    (s, e) => s + (e.cout_employeur || 0),
    0
  );
  const dsnNbEmployes = dsnEmployes.length;
  const dsnCoutMoyenParEmploye =
    dsnNbEmployes > 0 ? dsnTotalCoutEmployeur / dsnNbEmployes : 0;
  const dsnTauxMoyenCotisations =
    dsnNbEmployes > 0
      ? dsnEmployes.reduce((s, e) => s + (e.taux_moyen_cotisations || 0), 0) /
        dsnNbEmployes
      : 0;
  const dsnEqtpPeriode = dsnEffectif?.eqtp_sur_periode || 0;
  const dsnEqtpDernierJour = dsnEffectif?.eqtp_dernier_jour_mois || 0;

  // ── Helpers ──
  const esc = (str) =>
    (str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br/>");

  const fmt = (v, d = 0) =>
    Number(v).toLocaleString("fr-FR", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  const fmtE = (v) => fmt(v) + " €";
  const fmtPct = (v) => (v >= 0 ? "+" : "") + fmt(v, 1) + "%";
  const deltaClass = (v) => (v >= 0 ? "pos" : "neg");
  const deltaArrow = (v) => (v >= 0 ? "▲" : "▼");
  const pctCA = (val, ca) => {
    if (!ca || ca === 0) return "—";
    return fmt((val / ca) * 100, 1) + "%";
  };

  // ── Data chart ──
  const dsnChartLabels = useMemo(
    () => dsnEmployes.map((e) => `${e.nom} ${e.prenom}`),
    [dsnEmployes]
  );
  const dsnChartBrut = useMemo(
    () => dsnEmployes.map((e) => e.salaire_brut || 0),
    [dsnEmployes]
  );
  const dsnChartNet = useMemo(
    () => dsnEmployes.map((e) => e.net_a_payer || 0),
    [dsnEmployes]
  );
  const dsnChartCout = useMemo(
    () => dsnEmployes.map((e) => e.cout_employeur || 0),
    [dsnEmployes]
  );

  // ── Chart.js init ──
  useEffect(() => {
    let cancelled = false;

    const ensureChartJs = () =>
      new Promise((resolve, reject) => {
        if (window.Chart) return resolve(window.Chart);
        const existing = document.getElementById("chartjs-cdn");
        if (existing) {
          existing.addEventListener("load", () => resolve(window.Chart));
          existing.addEventListener("error", reject);
          return;
        }
        const s = document.createElement("script");
        s.id = "chartjs-cdn";
        s.src =
          "https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js";
        s.async = true;
        s.onload = () => resolve(window.Chart);
        s.onerror = reject;
        document.head.appendChild(s);
      });

    ensureChartJs().then((Chart) => {
      if (cancelled || !Chart || !barChartRef.current) return;

      Chart.defaults.font.family = "'DM Sans', system-ui, sans-serif";
      Chart.defaults.font.size = 11;
      Chart.defaults.color = "#8A849A";

      const GL = "#E6E3EC";

      if (barInstance.current) {
        barInstance.current.destroy();
      }

      barInstance.current = new Chart(barChartRef.current, {
        type: "bar",
        data: {
          labels: dsnChartLabels,
          datasets: [
            {
              label: "Salaire brut",
              data: dsnChartBrut,
              backgroundColor: "#DDD8E8",
              borderRadius: 4,
            },
            {
              label: "Net à payer",
              data: dsnChartNet,
              backgroundColor: "#A52050",
              borderRadius: 4,
            },
            {
              label: "Coût employeur",
              data: dsnChartCout,
              backgroundColor: "#7E1738",
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
              labels: { boxWidth: 10, padding: 12, font: { size: 10 } },
            },
            tooltip: {
              callbacks: {
                label: (ctx) =>
                  ctx.dataset.label +
                  " : " +
                  ctx.parsed.y.toLocaleString("fr-FR") +
                  " €",
              },
            },
          },
          scales: {
            y: {
              grid: { color: GL },
              border: { display: false },
              ticks: {
                callback: (v) => v.toLocaleString("fr-FR") + " €",
                font: { size: 10 },
              },
            },
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                font: { size: 10 },
                maxRotation: 30,
                minRotation: 0,
              },
            },
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (barInstance.current) {
        barInstance.current.destroy();
        barInstance.current = null;
      }
    };
  }, [dsnChartLabels, dsnChartBrut, dsnChartNet, dsnChartCout]);

  return (
    <div className="social-wrapper">
      <style>{`
        .social-wrapper {
          --brand:#7E1738; --brand-mid:#9B1F44;
          --bg:#F5F3F7;
          --border:#E6E3EC; --border-light:#F0EDF4;
          --text:#1A1525; --text-2:#4A4460; --muted:#8A849A;
          --blue:#2563EB; --blue-bg:#EFF6FF; --blue-mid:#DBEAFE;
          --green:#16A34A; --green-bg:#F0FDF4; --green-mid:#DCFCE7;
          --amber:#D97706; --amber-bg:#FFFBEB; --amber-mid:#FEF3C7;
          --red:#DC2626; --red-bg:#FEF2F2; --red-mid:#FEE2E2;
          --purple:#7C3AED; --purple-bg:#F5F3FF; --purple-mid:#EDE9FE;
          --teal:#0891B2; --teal-bg:#ECFEFF; --teal-mid:#CFFAFE;
          --indigo:#4F46E5; --indigo-bg:#EEF2FF; --indigo-mid:#C7D2FE;
          --radius:10px;
          font-family:'DM Sans',system-ui,sans-serif;
          color:var(--text);
          font-size:13px;
          padding:20px 28px 32px;
          background:var(--bg);
          display:flex;
          flex-direction:column;
          gap:16px;
        }

        /* Header DSN */
        .sc-header{
          display:flex;
          align-items:center;
          justify-content:space-between;
          flex-wrap:wrap;
          gap:10px;
        }
        .sc-header-title{
          font-size:13px;
          font-weight:600;
          color:var(--text);
        }
        .sc-header-siret{
          font-size:11px;
          font-weight:400;
          color:var(--muted);
          margin-left:8px;
        }
        .sc-periode{
          font-size:11px;
          color:var(--muted);
          margin-top:2px;
        }

        /* KPI GRID */
        .sc-kpi-grid{
          display:grid;
          grid-template-columns:repeat(4,1fr);
          gap:14px;
        }
        .sc-kpi-grid-2{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:14px;
        }
        .sc-kpi-card{
          background:var(--kpi-bg,var(--blue-bg));
          border:1px solid var(--kpi-mid,var(--blue-mid));
          border-radius:var(--radius);
          padding:16px 18px;
          display:flex;
          flex-direction:column;
          gap:3px;
          transition:transform .15s,box-shadow .15s;
        }
        .sc-kpi-card:hover{
          transform:translateY(-1px);
          box-shadow:0 4px 16px rgba(0,0,0,.07);
        }
        .sc-kpi-number{
          font-size:22px;
          font-weight:700;
          color:var(--kpi-color,var(--blue));
          line-height:1.1;
          letter-spacing:-.02em;
        }
        .sc-kpi-lbl{
          font-size:11px;
          font-weight:500;
          color:var(--kpi-color,var(--blue));
          opacity:.75;
          margin-top:1px;
        }
        .sc-kpi-delta{
          display:inline-flex;
          align-items:center;
          font-size:10.5px;
          font-weight:500;
          padding:2px 8px;
          border-radius:20px;
          margin-top:5px;
          width:fit-content;
        }
        .sc-kpi-delta.neg{background:rgba(220,38,38,.1);color:#B91C1C}
        .sc-kpi-delta.pos{background:rgba(22,163,74,.1);color:#15803D}
        .sc-kpi-delta.neu{background:rgba(100,100,120,.08);color:var(--text-2)}
        .kpi-blue{--kpi-bg:var(--blue-bg);--kpi-mid:var(--blue-mid);--kpi-color:var(--blue)}
        .kpi-teal{--kpi-bg:var(--teal-bg);--kpi-mid:var(--teal-mid);--kpi-color:var(--teal)}
        .kpi-purple{--kpi-bg:var(--purple-bg);--kpi-mid:var(--purple-mid);--kpi-color:var(--purple)}
        .kpi-amber{--kpi-bg:var(--amber-bg);--kpi-mid:var(--amber-mid);--kpi-color:var(--amber)}

        /* Layout principal */
        .sc-main{
          display:grid;
          grid-template-columns:62% 38%;
          gap:16px;
          align-items:start;
        }

        .sc-col{
          display:flex;
          flex-direction:column;
          gap:14px;
        }

        /* Cards */
        .sc-card{
          background:#fff;
          border:1px solid var(--border);
          border-radius:var(--radius);
          overflow:hidden;
        }
        .sc-card-padded{
          padding:18px 20px;
        }
        .sc-card-sm{
          padding:14px 16px;
        }
        .sc-card-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding:14px 18px 10px;
        }
        .sc-card-title{
          font-size:12.5px;
          font-weight:600;
          color:var(--text);
        }
        .sc-card-sub{
          font-size:11px;
          color:var(--muted);
        }
        .sc-sec-label{
          font-size:10px;
          font-weight:700;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:var(--muted);
          padding:4px 0 10px;
          border-bottom:1px solid var(--border);
          margin-bottom:14px;
        }

        /* Table */
        .sc-tbl{
          width:100%;
          border-collapse:collapse;
          font-size:12.5px;
        }
        .sc-tbl thead th{
          background:var(--bg);
          color:var(--text-2);
          padding:9px 12px;
          text-align:left;
          font-size:11px;
          font-weight:600;
          letter-spacing:.04em;
          border-bottom:1px solid var(--border);
          border-top:1px solid var(--border);
          white-space:nowrap;
        }
        .sc-tbl thead th:not(:first-child){text-align:right}
        .sc-tbl td{
          padding:9px 12px;
          color:var(--text-2);
          border-bottom:1px solid var(--border-light);
          vertical-align:middle;
        }
        .sc-tbl td:not(:first-child){
          text-align:right;
          font-variant-numeric:tabular-nums;
        }
        .sc-tbl tbody tr:last-child td{border-bottom:none}
        .sc-tbl tbody tr:hover{background:var(--bg)}
        .sc-tbl .tr-g td{background:#F0EDF4!important;color:var(--text);font-weight:600}
        .sc-tbl .tr-tot td{
          background:var(--brand)!important;
          color:rgba(255,255,255,.95)!important;
          font-weight:600;
        }
        .sc-rank{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:22px;
          height:22px;
          border-radius:50%;
          background:var(--border-light);
          color:var(--text-2);
          font-size:10.5px;
          font-weight:700;
          margin-right:8px;
        }
        .sc-rank-1{background:#FEF3C7;color:#92400E}
        .sc-rank-2{background:#E5E7EB;color:#374151}
        .sc-rank-3{background:#FED7AA;color:#9A3412}
        .sc-cout-cell{
          font-weight:600;
          color:var(--brand);
        }

        /* Bilan rows */
        .sc-bilan-row{
          display:flex;
          justify-content:space-between;
          align-items:baseline;
          padding:7px 0;
          font-size:12.5px;
          border-bottom:1px solid var(--border-light);
        }
        .sc-bilan-label{color:var(--muted)}
        .sc-bilan-val{
          font-weight:600;
          color:var(--text);
          font-variant-numeric:tabular-nums;
        }

        /* Chart */
        .sc-chart-box{
          position:relative;
          height:220px;
        }
        .sc-chart-box canvas{
          width:100%!important;
          display:block;
        }

        /* Tiles */
        .sc-tile-analyse{
          background:var(--indigo-bg);
          border:1px solid var(--indigo-mid);
          border-left:3px solid var(--indigo);
          border-radius:var(--radius);
          padding:16px 18px;
        }
        .sc-tile-analyse-head{
          display:flex;
          align-items:center;
          gap:8px;
          margin-bottom:10px;
        }
        .sc-tile-analyse-lbl{
          font-size:10px;
          font-weight:700;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:var(--indigo);
        }
        .sc-tile-analyse p{
          font-size:12.5px;
          line-height:1.8;
          color:#3730A3;
        }
        .sc-tile-infos{
          background:#FDF0F3;
          border:1px solid #F2D0DA;
          border-left:3px solid var(--brand);
          border-radius:var(--radius);
          padding:16px 18px;
        }
        .sc-tile-infos-head{
          font-size:10px;
          font-weight:700;
          letter-spacing:.14em;
          text-transform:uppercase;
          color:var(--brand);
          margin-bottom:10px;
        }
        .sc-tile-infos p{
          font-size:12.5px;
          line-height:1.8;
          color:var(--text-2);
        }
      `}</style>

      {/* ── Header ── */}
      <div className="sc-header">
        <div>
          <div className="sc-header-title">
            {esc(dsn?.entreprise?.nom || "")}
            {dsn?.entreprise?.siret && (
              <span className="sc-header-siret">
                SIRET : {esc(dsn.entreprise.siret)}
              </span>
            )}
          </div>
          {dsnPeriode.debut && (
            <div className="sc-periode">
              Période DSN : {esc(dsnPeriode.debut)} → {esc(dsnPeriode.fin)}
            </div>
          )}
        </div>
      </div>

      {/* ── KPI GRID ── */}
      <div className="sc-kpi-grid">
        <div className="sc-kpi-card kpi-blue">
          <div className="sc-kpi-number">{dsnNbEmployes}</div>
          <div className="sc-kpi-lbl">Salariés (DSN)</div>
          <span className="sc-kpi-delta neu">
            EQTP période : {fmt(dsnEqtpPeriode, 2)}
          </span>
        </div>

        <div className="sc-kpi-card kpi-teal">
          <div className="sc-kpi-number">{fmt(dsnEqtpDernierJour, 2)}</div>
          <div className="sc-kpi-lbl">EQTP dernier jour</div>
          <span className="sc-kpi-delta neu">
            EQTP sur période : {fmt(dsnEqtpPeriode, 2)}
          </span>
        </div>

        <div className="sc-kpi-card kpi-purple">
          <div className="sc-kpi-number" style={{ fontSize: 18 }}>
            {fmtE(dsnTotalBrut)}
          </div>
          <div className="sc-kpi-lbl">Masse salariale brute (DSN)</div>
          <span className="sc-kpi-delta neu">
            Net à payer : {fmtE(dsnTotalNet)}
          </span>
        </div>

        <div className="sc-kpi-card kpi-amber">
          <div className="sc-kpi-number" style={{ fontSize: 18 }}>
            {fmtE(dsnTotalCoutEmployeur)}
          </div>
          <div className="sc-kpi-lbl">Coût employeur total (DSN)</div>
          <span className="sc-kpi-delta neu">
            Moy./salarié : {fmtE(dsnCoutMoyenParEmploye)}
          </span>
        </div>
      </div>

      {/* ── LAYOUT PRINCIPAL ── */}
      <div className="sc-main">
        {/* COLONNE GAUCHE */}
        <div className="sc-col">
          {/* Tableau collaborateurs triés par coût employeur */}
          <div className="sc-card">
            <div className="sc-card-head">
              <div className="sc-card-title">
                Détail par collaborateur — DSN
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 500,
                    color: "var(--muted)",
                    marginLeft: 8,
                  }}
                >
                  trié par coût employeur ↓
                </span>
              </div>
              <span className="sc-card-sub">
                {esc(dsnPeriode.debut)} → {esc(dsnPeriode.fin)}
              </span>
            </div>
            <table className="sc-tbl">
              <thead>
                <tr>
                  <th>Collaborateur</th>
                  <th>Brut</th>
                  <th>Cotis. sal.</th>
                  <th>Cotis. pat.</th>
                  <th>Net à payer</th>
                  <th>Coût empl.</th>
                  <th>Tx moy. cotis.</th>
                </tr>
              </thead>
              <tbody>
                {dsnEmployes.map((emp, idx) => (
                  <tr key={`${emp.nom}-${emp.prenom}-${idx}`}>
                    <td style={{ fontWeight: 500 }}>
                      {emp.nom} {emp.prenom}
                    </td>
                    <td>{fmtE(emp.salaire_brut || 0)}</td>
                    <td>{fmtE(emp.cotisations_salariales || 0)}</td>
                    <td>{fmtE(emp.cotisations_patronales || 0)}</td>
                    <td>{fmtE(emp.net_a_payer || 0)}</td>
                    <td className="sc-cout-cell">
                      {fmtE(emp.cout_employeur || 0)}
                    </td>
                    <td>{fmt(emp.taux_moyen_cotisations || 0, 2)}%</td>
                  </tr>
                ))}
                <tr className="tr-tot">
                  <td>Total</td>
                  <td>{fmtE(dsnTotalBrut)}</td>
                  <td>{fmtE(dsnTotalCotisationsSal)}</td>
                  <td>{fmtE(dsnTotalCotisationsPat)}</td>
                  <td>{fmtE(dsnTotalNet)}</td>
                  <td>{fmtE(dsnTotalCoutEmployeur)}</td>
                  <td>{fmt(dsnTauxMoyenCotisations, 2)}%</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Décomposition masse salariale */}
          <div className="sc-card">
            <div className="sc-card-head">
              <div className="sc-card-title">
                Décomposition de la masse salariale
              </div>
            </div>
            <table className="sc-tbl">
              <thead>
                <tr>
                  <th>Nature</th>
                  <th>Montant</th>
                  <th>% du brut</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Salaires bruts</td>
                  <td>{fmtE(dsnTotalBrut)}</td>
                  <td style={{ color: "var(--muted)" }}>100,0%</td>
                </tr>
                <tr>
                  <td>Cotisations salariales</td>
                  <td>{fmtE(dsnTotalCotisationsSal)}</td>
                  <td style={{ color: "var(--muted)" }}>
                    {dsnTotalBrut > 0
                      ? fmt((dsnTotalCotisationsSal / dsnTotalBrut) * 100, 1) +
                        "%"
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>Net à payer</td>
                  <td>{fmtE(dsnTotalNet)}</td>
                  <td style={{ color: "var(--muted)" }}>
                    {dsnTotalBrut > 0
                      ? fmt((dsnTotalNet / dsnTotalBrut) * 100, 1) + "%"
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>Cotisations patronales</td>
                  <td>{fmtE(dsnTotalCotisationsPat)}</td>
                  <td style={{ color: "var(--muted)" }}>
                    {dsnTotalBrut > 0
                      ? fmt((dsnTotalCotisationsPat / dsnTotalBrut) * 100, 1) +
                        "%"
                      : "—"}
                  </td>
                </tr>
                <tr className="tr-tot">
                  <td>Coût employeur total</td>
                  <td>{fmtE(dsnTotalCoutEmployeur)}</td>
                  <td>
                    {dsnTotalBrut > 0
                      ? fmt((dsnTotalCoutEmployeur / dsnTotalBrut) * 100, 1) +
                        "%"
                      : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Graphique */}
          <div className="sc-card sc-card-padded">
            <div
              className="sc-card-head"
              style={{ padding: 0, marginBottom: 14 }}
            >
              <div className="sc-card-title">
                Coût employeur par collaborateur
              </div>
              <span className="sc-card-sub">Brut vs Net vs Coût total</span>
            </div>
            <div className="sc-chart-box">
              <canvas ref={barChartRef} />
            </div>
          </div>
        </div>

        {/* COLONNE DROITE */}
        <div className="sc-col">
          {/* KPI 2 cols */}
          <div className="sc-kpi-grid-2">
            <div
              className="sc-kpi-card kpi-blue"
              style={{ padding: "14px 16px" }}
            >
              <div className="sc-kpi-number" style={{ fontSize: 18 }}>
                {fmtE(chargesPersonnelN)}
              </div>
              <div className="sc-kpi-lbl" style={{ fontSize: 11 }}>
                Personnel bilan {exerciceN}
              </div>
              <span
                className={`sc-kpi-delta ${deltaClass(chargesPersonnelVarPct)}`}
                style={{ fontSize: 10 }}
              >
                {deltaArrow(chargesPersonnelVarPct)}{" "}
                {fmtPct(chargesPersonnelVarPct)}
              </span>
            </div>
            <div
              className="sc-kpi-card kpi-purple"
              style={{ padding: "14px 16px" }}
            >
              <div className="sc-kpi-number" style={{ fontSize: 18 }}>
                {pctCA(chargesPersonnelN, caN)}
              </div>
              <div className="sc-kpi-lbl" style={{ fontSize: 11 }}>
                % du CA {exerciceN}
              </div>
              <span className="sc-kpi-delta neu" style={{ fontSize: 10 }}>
                vs {pctCA(chargesPersonnelN1, caN1)}
              </span>
            </div>
          </div>

          {/* Synthèse effectif */}
          <div className="sc-card sc-card-sm">
            <div className="sc-sec-label">Effectif — Synthèse DSN</div>
            <div className="sc-bilan-row">
              <span className="sc-bilan-label">Nombre de collaborateurs</span>
              <span className="sc-bilan-val">{dsnNbEmployes}</span>
            </div>
            <div className="sc-bilan-row">
              <span className="sc-bilan-label">EQTP sur la période</span>
              <span className="sc-bilan-val">{fmt(dsnEqtpPeriode, 2)}</span>
            </div>
            <div className="sc-bilan-row">
              <span className="sc-bilan-label">EQTP dernier jour du mois</span>
              <span className="sc-bilan-val">{fmt(dsnEqtpDernierJour, 2)}</span>
            </div>
            <div className="sc-bilan-row">
              <span className="sc-bilan-label">Coût moyen / collaborateur</span>
              <span className="sc-bilan-val">
                {fmtE(dsnCoutMoyenParEmploye)}
              </span>
            </div>
            <div className="sc-bilan-row">
              <span className="sc-bilan-label">Taux moyen cotisations</span>
              <span className="sc-bilan-val">
                {fmt(dsnTauxMoyenCotisations, 2)}%
              </span>
            </div>
            <div className="sc-bilan-row" style={{ borderBottom: "none" }}>
              <span className="sc-bilan-label">Personnel / CA {exerciceN}</span>
              <span className="sc-bilan-val" style={{ color: "var(--brand)" }}>
                {pctCA(chargesPersonnelN, caN)}
              </span>
            </div>
          </div>

          {/* Tableau comparatif N / N-1 */}
          <div className="sc-card">
            <div className="sc-card-head">
              <div className="sc-card-title">
                Charges personnel bilan {exerciceN1} / {exerciceN}
              </div>
            </div>
            <table className="sc-tbl">
              <thead>
                <tr>
                  <th>Poste</th>
                  <th>{exerciceN}</th>
                  <th>{exerciceN1}</th>
                  <th>Évol.</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Charges personnel</td>
                  <td>{fmtE(chargesPersonnelN)}</td>
                  <td style={{ color: "var(--muted)" }}>
                    {fmtE(chargesPersonnelN1)}
                  </td>
                  <td className={deltaClass(chargesPersonnelVarPct)}>
                    {fmtPct(chargesPersonnelVarPct)}
                  </td>
                </tr>
                <tr className="tr-g">
                  <td>% CA</td>
                  <td>{pctCA(chargesPersonnelN, caN)}</td>
                  <td style={{ color: "var(--muted)" }}>
                    {pctCA(chargesPersonnelN1, caN1)}
                  </td>
                  <td style={{ color: "var(--muted)" }}>—</td>
                </tr>
                <tr>
                  <td>% Valeur ajoutée</td>
                  <td>{pctCA(chargesPersonnelN, valeurAjouteeN)}</td>
                  <td style={{ color: "var(--muted)" }}>
                    {pctCA(chargesPersonnelN1, valeurAjouteeN1)}
                  </td>
                  <td style={{ color: "var(--muted)" }}>—</td>
                </tr>
                <tr className="tr-tot">
                  <td>Variation absolue</td>
                  <td colSpan={2} style={{ textAlign: "center" }}>
                    {fmtE(Math.abs(chargesPersonnelN - chargesPersonnelN1))}
                  </td>
                  <td>{fmtPct(chargesPersonnelVarPct)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Tiles */}

          <div
            className="rounded-md border-l-[3px] p-4"
            style={{ backgroundColor: "#EEF2FF", borderLeftColor: "#4F46E5" }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                Analyse
              </p>

              {isDirty.analyseSocial && (
                <span className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px] px-2 py-0.5 rounded-md shadow-sm">
                  Modifié
                </span>
              )}
            </div>

            <AutoResizeTextarea
              value={editableResult?.social_personnel?.analyse || ""}
              onChange={(e) => {
                setIsDirty((prev) => ({
                  ...prev,
                  analyseSocial: true,
                }));
                setEditableResult((prev) => ({
                  ...prev,
                  social_personnel: {
                    ...prev.social_personnel,
                    analyse: e.target.value,
                  },
                }));
              }}
              className="text-indigo-900/80"
            />
          </div>

          <div
            className="rounded-md border-l-[3px] p-4"
            style={{ backgroundColor: "#FDF0F3", borderLeftColor: "#7E1738" }}
          >
            <div className="flex items-center justify-between mb-2">
              <p
                className="text-xs font-semibold uppercase tracking-wide"
                style={{ color: "#7E1738" }}
              >
                Informations & Recommandations
              </p>

              {isDirty.informations_recommandationsSocial && (
                <span className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px] px-2 py-0.5 rounded-md shadow-sm">
                  Modifié
                </span>
              )}
            </div>

            <AutoResizeTextarea
              value={
                editableResult?.social_personnel
                  ?.informations_recommandations || ""
              }
              onChange={(e) => {
                setIsDirty((prev) => ({
                  ...prev,
                  informations_recommandationsSocial: true,
                }));
                setEditableResult((prev) => ({
                  ...prev,
                  social_personnel: {
                    ...prev.social_personnel,
                    informations_recommandations: e.target.value,
                  },
                }));
              }}
              className="text-foreground/80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelSocial;
